import { GateMovementState2 as MovementState } from '@/__generated__/graphql';
import { OverlayState } from '@components/GateFigure';
import { AU_ERROR, AU_SUCCESS, AU_TEXT } from '@/theme/au-compat';
import { RED } from '@features/gateOperationModal/constants';
import { type I18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import { CheckCircleOutline, HighlightOff, Login } from '@mui/icons-material';
import { teal } from '@mui/material/colors';
import { InMovementIcon } from '../components/figure/InMovementIcon';
import { InQueueIcon } from '../components/figure/InQueueIcon';
import { Accent, ErrorAccent } from './accents';
import { describeGateCloseCode, type GateCloseDescription } from './gateCloseCode';

const FINALIZED_STATES = [MovementState.Finalized, MovementState.FinalizedWithError];
const INITIALIZING_STATES = [MovementState.InQueue, MovementState.Initializing];

export const translateMovementState = (state: MovementState, i18n: I18n) => {
  const translationMap: Record<MovementState, string> = {
    [MovementState.InQueue]: t(i18n)`En cola`,
    [MovementState.Initializing]: t(i18n)`Inicializando`,
    [MovementState.InMovement]: t(i18n)`En movimiento`,
    [MovementState.Finalized]: t(i18n)`Finalizada`,
    [MovementState.FinalizedWithError]: t(i18n)`Finalizada con error`,
  };
  return translationMap[state];
};

export const isFinalizedState = (state: MovementState) => {
  return FINALIZED_STATES.includes(state);
};

export const isInitializingState = (state: MovementState) => {
  return INITIALIZING_STATES.includes(state);
};

/**
 * `true` si la solicitud en cola (SGMO-14) pasó su plazo (`expiresAt <= now`, mismo criterio que
 * `queuedRequestDrainability.ts` de SGMO) pero **todavía no está cerrada**. Una fila `IN_QUEUE` sin
 * `expiresAt` —anterior a SGMO-14 o fuera del carril— no tiene plazo: se sigue viendo «En cola».
 *
 * **Pasar el plazo no es estar vencida.** El despachador de SGMO revisa el plazo solo al empezar un
 * intento (`drain.ts`), así que uno que arrancó justo antes puede terminar con accept después; y el
 * cierre `QEXPIRED` lo escribe recién el próximo barrido o reconexión. Hasta ese cierre persistido
 * la UI no puede afirmar que la solicitud no se enviará (D-010).
 */
export const isQueuedRequestPastDeadline = (
  movement: { state: MovementState; expiresAt?: number | null },
  now: number,
) =>
  movement.state === MovementState.InQueue &&
  movement.expiresAt != null &&
  movement.expiresAt <= now;

type MovementStateColor = {
  color: string;
  contrastColor: string;
};

/**
 * El color del estado como **marca gráfica**: el relleno de algo que no lleva texto encima —hoy,
 * las franjas de la barra de progreso—, medido a 3:1 porque ahí el color se ve y no se lee.
 *
 * Los valores son los de la capa (`AU_TEXT` / `AU_SUCCESS` / `AU_ERROR`, la base pelada de cada
 * tono) y no los `#ffffff` / `#26A69A` / `#923E4A` que este archivo tenía: aquel verde y aquel rojo
 * eran de la paleta anterior, así que la barra terminaba de un verde y la banda de estado que la
 * acompaña, de otro.
 *
 * Para todo lo que lleva **texto encima** el canal es el otro: `stateTone` + `auToneSurfaceSx`.
 */
export const getStateColor = (state: MovementState) => {
  /** `--au-text`: las tres etapas en vuelo comparten la marca acromática. */
  const neutralColors: MovementStateColor = {
    color: AU_TEXT,
    contrastColor: '#000000',
  };

  const successColors: MovementStateColor = {
    color: AU_SUCCESS,
    contrastColor: '#ffffff',
  };

  const errorColors: MovementStateColor = {
    color: AU_ERROR,
    contrastColor: '#ffffff',
  };

  const colorMap: Record<MovementState, MovementStateColor> = {
    [MovementState.InQueue]: neutralColors,
    [MovementState.Initializing]: neutralColors,
    [MovementState.InMovement]: neutralColors,
    [MovementState.Finalized]: successColors,
    [MovementState.FinalizedWithError]: errorColors,
  };

  return colorMap[state];
};

/** Los tres tonos que un estado de movimiento puede tomar. Ver `stateTone`. */
export type MovementTone = 'info' | 'success' | 'error';

const STATE_TONE: Record<MovementState, MovementTone> = {
  [MovementState.InQueue]: 'info',
  [MovementState.Initializing]: 'info',
  [MovementState.InMovement]: 'info',
  [MovementState.Finalized]: 'success',
  [MovementState.FinalizedWithError]: 'error',
};

/**
 * El tono de un estado de movimiento, para lo que lleva **texto encima**: el chip de la tabla.
 *
 * Es el hermano de `getStateColor`, que sigue vivo porque `gateOperationModal` y la figura lo
 * consumen para pintar rellenos sólidos. La diferencia no es de estilo sino de trabajo: aquél
 * devuelve un color como marca gráfica —se mide a 3:1 porque ahí el color se ve—, y esto devuelve
 * el nombre de un tono, que la superficie resuelve contra `AU_TINT` a 4,5:1 porque ahí el color se
 * lee. Tenerlos separados es lo que evita que el verde del chip y el verde de la figura sean dos
 * verdes distintos.
 *
 * **Los cinco estados tienen tono, y las tres etapas en vuelo comparten `info`**: una operación que
 * está pasando no es una noticia ni buena ni mala. Que `info` se repita tres veces no es ruido —
 * hace visible que comparten tono, que en una cadena de `if` con `return` final quedaba implícito.
 *
 * `Record<MovementState, …>` y no una cadena de `if`: un sexto estado sin tono no compila. El
 * espécimen se puso esa red después de descubrir que un `return` de salida dejaba caer estados
 * nuevos en `info` sin avisar.
 *
 * Portado de `digital-product-guidelines @ 9e0549f` — `src/pages/operar-compuerta/state-tone.ts`.
 */
export const stateTone = (state: MovementState): MovementTone => STATE_TONE[state];

/**
 * Rótulo y tono de un movimiento leyendo, además del `state`, el código de cierre y el
 * vencimiento (AMARU-897/US-006). `state` colapsa cierres muy distintos: una solicitud en cola
 * cancelada (`QCANCEL`) o vencida (`QEXPIRED`) cierra `FINALIZED` sin haber movido nada, y sin el
 * código se leía «Finalizada» en verde. Con un cierre que no es un cumplimiento manda el código
 * (tono `error`); con uno que sí lo es (GO01/GO02/QDUPMID) o sin código, queda el estado. Una fila
 * `IN_QUEUE` que pasó su plazo se ve «Venció el plazo» en tono neutro: «vencida» en rojo queda para
 * el cierre `QEXPIRED` persistido (D-010).
 *
 * Es la regla única del historial y del panel de operación: si divergen, la misma operación se ve
 * de un color en un lado y de otro en el otro. Devuelve también el cierre ya resuelto (`close`)
 * para que quien necesite su título o detalle no lo vuelva a describir por su cuenta.
 */
export const describeMovementStatus = (
  movement: { state: MovementState; closeCode?: string | null; expiresAt?: number | null },
  i18n: I18n,
  now: number,
): {
  label: string;
  tone: MovementTone;
  pastDeadline: boolean;
  close: GateCloseDescription | null;
} => {
  if (isQueuedRequestPastDeadline(movement, now)) {
    return { label: t(i18n)`Venció el plazo`, tone: 'info', pastDeadline: true, close: null };
  }
  const close = isFinalizedState(movement.state)
    ? describeGateCloseCode(movement.closeCode, i18n)
    : null;
  if (close && close.outcome !== 'completed') {
    return { label: close.title, tone: 'error', pastDeadline: false, close };
  }
  return {
    label: translateMovementState(movement.state, i18n),
    tone: stateTone(movement.state),
    pastDeadline: false,
    close,
  };
};

export const overlayStateMap = (state: MovementState) => {
  const translationMap: Record<MovementState, OverlayState> = {
    [MovementState.InQueue]: {
      id: 1,
      icon: InQueueIcon,
      // Una fila `IN_QUEUE` es una solicitud encolada a la espera de que el equipo se conecte
      // (SGMO-14), que puede durar horas: «iniciando conexión» la presentaba como un movimiento
      // arrancando.
      label: (
        <Trans>
          Solicitud <Accent>en cola</Accent>
          <br />a la espera del equipo
        </Trans>
      ),
      iconColor: 'white',
      iconContrastColor: 'black',
    },
    [MovementState.Initializing]: {
      id: 2,
      icon: Login,
      label: (
        <Trans>
          <Accent>Inicio de operación</Accent> de la compuerta
        </Trans>
      ),
      iconColor: 'white',
      iconContrastColor: 'black',
    },
    [MovementState.InMovement]: {
      id: 3,
      icon: InMovementIcon,
      label: (
        <Trans>
          Compuerta en
          <br />
          <Accent>movimiento</Accent>
        </Trans>
      ),
      iconColor: 'white',
      iconContrastColor: 'black',
    },
    [MovementState.Finalized]: {
      id: 4,
      icon: CheckCircleOutline,
      label: (
        <Trans>
          Operación
          <br />
          <Accent>finalizada</Accent>
        </Trans>
      ),
      iconColor: teal[400],
      iconContrastColor: 'white',
    },
    [MovementState.FinalizedWithError]: {
      id: 5,
      icon: HighlightOff,
      label: (
        <Trans>
          Operación <ErrorAccent>finalizada con errores</ErrorAccent>
        </Trans>
      ),
      iconColor: RED,
      iconContrastColor: 'white',
    },
  };
  return translationMap[state];
};
